import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { adminApi } from '../../../services/api';
import SalesAgGridTable from '../SalesAgGridTable';
import EntityEditor from '../EntityEditor';
import RetellCallPanel from '../RetellCallPanel';
import SidebarPanel from '../../admin/SidebarPanel';
import SalesModuleSettings from '../SalesModuleSettings';
import AlertMessage from '../../shared/alertMessage';
import ActionsCellRenderer from './ActionsCellRenderer';
import { buildSalesColumns, defaultColDef } from './SalesTableColumns';
import { transformSalesData } from '../../../utils/SalesTableUtils';
import { getDateRangePreset, getFilteredPresets, isValidDateRange } from '../../../utils/calendarUtils';

/**
 * Shared Sales Table Page Component
 * Used by both SalesLeadsPage and SalesOpportunityPage
 *
 * @param {Object} config - Configuration object
 * @param {string} config.entityType - 'lead' or 'opportunity'
 * @param {string} config.entityName - Display name (e.g., 'Sales Leads', 'Sales Opportunities')
 * @param {string} config.basePath - Base URL path (e.g., '/admin/sales/leads')
 * @param {Object} config.api - API methods { getAll, getOne, update, delete }
 * @param {Object} config.redux - Redux selectors and actions
 */
const SalesTablePage = ({ config }) => {
  const {
    entityType,
    entityName,
    entityNamePlural,
    basePath,
    api,
    redux
  } = config;

  const gridRef = useRef(null);
  const activeCallStatusesRef = useRef({}); // { [entityId]: { callId, status } }
  const pendingPollsRef = useRef(new Set());  // entityIds whose calls still need polling
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const settingsOpen = searchParams.get('settings') === 'true';
  const urlPanel = searchParams.get('panel');
  const urlEntityId = searchParams.get('id');

  // Redux state
  const dataFromRedux = useSelector(redux.selectData);
  const editedRows = useSelector(redux.selectEditedRows);
  const editedRowsCount = useSelector(redux.selectEditedRowsCount);
  const loading = useSelector(redux.selectLoading);
  const refreshing = useSelector(redux.selectRefreshing);
  const saving = useSelector(redux.selectSaving);
  const error = useSelector(redux.selectError);

  // Sidebar state
  const [sidePanel, setSidePanel] = useState({
    isOpen: false,
    type: null
  });

  // Filter state
  const [filters, setFilters] = useState({
    created_by: '',
    assigned_to: '',
    is_active: 'true', // Default to showing only active records
    activity_date_from: '',
    activity_date_to: ''
  });

  // Date range preset selection
  const [selectedPreset, setSelectedPreset] = useState(null);

  // Filtered presets for this page
  const dateRangePresets = getFilteredPresets(['today', 'yesterday', '1week', '2weeks', '1month']);

  // Settings state - initialize to null to distinguish "not loaded" from "empty array"
  const [contactStatuses, setContactStatuses] = useState(null);
  const [priorities, setPriorities] = useState(null);
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  // Local UI state
  const [selectedEntityId, setSelectedEntityId] = useState(null);
  const [showFormEditor, setShowFormEditor] = useState(false);
  const [showCallModal, setShowCallModal] = useState(false);
  const [alertState, setAlertState] = useState({ show: false, type: '', title: '', message: '' });
  const [totalCount, setTotalCount] = useState(0); // Total count for pagination and stats
  const [loadingMore, setLoadingMore] = useState(false); // Loading state for "Load More" button

  // Computed data from Redux
  // List data from API is already flat and lightweight - no need for deep copy
  // AG Grid handles its own internal mutations for cell editing
  const data = useMemo(() => {
    return dataFromRedux;
  }, [dataFromRedux]);

  // Refresh a single row's cells in the grid
  const refreshCallRow = useCallback((entityId) => {
    if (gridRef.current?.api) {
      const rowNode = gridRef.current.api.getRowNode(String(entityId));
      if (rowNode) {
        gridRef.current.api.refreshCells({ rowNodes: [rowNode], force: true });
      }
    }
  }, []);

  // Track active call statuses per entity so the phone button reflects call state.
  // Also registers/deregisters the call in pendingPollsRef so the master interval
  // keeps polling even after the panel is closed.
  const TERMINAL_STATUSES = ['ended', 'error', 'canceled', 'completed', 'failed', 'cancelled'];
  const handleCallStatusChange = useCallback((entityId, { callId, status }) => {
    const key = String(entityId);
    activeCallStatusesRef.current = {
      ...activeCallStatusesRef.current,
      [key]: { callId, status }
    };
    if (TERMINAL_STATUSES.includes(status)) {
      pendingPollsRef.current.delete(key);
    } else {
      pendingPollsRef.current.add(key);
    }
    refreshCallRow(key);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshCallRow]);

  // Master background polling — runs for every entity in pendingPollsRef.
  // Survives panel open/close so calls initiated and then dismissed still update.
  // NOTE: For >20 concurrent calls, consider replacing this polling approach with
  // WebSocket/Django Channels consumer broadcasting from Retell webhook handlers.
  // See zoommgr/consumers.py for reference implementation.
  useEffect(() => {
    const interval = setInterval(async () => {
      const entityIds = [...pendingPollsRef.current];
      if (entityIds.length === 0) return;

      // Fetch all call statuses in parallel for better performance
      await Promise.all(entityIds.map(async (entityId) => {
        const callInfo = activeCallStatusesRef.current[entityId];
        if (!callInfo?.callId) {
          pendingPollsRef.current.delete(entityId);
          return;
        }
        // Check if this is a queued call (queue ID is a UUID, call IDs start with "call_")
        const isQueueId = !callInfo.callId.startsWith('call_');

        try {
          let response;
          if (isQueueId) {
            // For queued calls, poll the queue endpoint instead of Retell
            response = await adminApi.getCallQueueItem(callInfo.callId);
            if (response.data?.success) {
              const queueData = response.data.call;
              const newStatus = queueData.status;
              const retellCallId = queueData.retell_call_id;

              activeCallStatusesRef.current = {
                ...activeCallStatusesRef.current,
                [entityId]: {
                  callId: retellCallId || callInfo.callId, // Upgrade to real call ID when available
                  status: newStatus,
                  queue_position: queueData.queue_position
                }
              };

              if (TERMINAL_STATUSES.includes(newStatus)) {
                pendingPollsRef.current.delete(entityId);
              }
              refreshCallRow(entityId);
            }
          } else {
            // For active/completed calls, poll Retell call status
            const response = await adminApi.getRetellCallStatus(callInfo.callId);
            if (response.data?.success) {
              const newStatus = response.data.data?.call_status;
              activeCallStatusesRef.current = {
                ...activeCallStatusesRef.current,
                [entityId]: { callId: callInfo.callId, status: newStatus }
              };
              if (TERMINAL_STATUSES.includes(newStatus)) {
                pendingPollsRef.current.delete(entityId);
              }
              refreshCallRow(entityId);
            }
          }
        } catch (err) {
          // Stop polling if we get a 404 (call/queue item no longer exists)
          if (err.response?.status === 404) {
            console.warn(`[SalesTable] Call/queue ${callInfo.callId} not found (404), stopping poll for entity ${entityId}`);
            pendingPollsRef.current.delete(entityId);
            delete activeCallStatusesRef.current[entityId];
            refreshCallRow(entityId);
          } else {
            console.error(`[SalesTable] Background poll failed for entity ${entityId}:`, err);
          }
        }
      }));
    }, 3000);

    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshCallRow]); // stable — only runs once on mount

  // Handle URL parameter - automatically open sidebar if settingsOpen is in URL
  useEffect(() => {
    if (settingsOpen && !sidePanel.isOpen) {
      setSidePanel({ isOpen: true, type: 'settings' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsOpen, sidePanel.isOpen]);

  // Handle URL parameters for panels
  useEffect(() => {
    // Handle create panel (no entityId needed)
    if (urlPanel === 'create' && !showFormEditor) {
      setSelectedEntityId(null);
      setShowFormEditor(true);
      return;
    }

    // Handle other panels that require an entity ID
    if (urlPanel && urlEntityId && data.length > 0) {
      // IDs can be either integers or UUIDs depending on the model
      const entityId = isNaN(urlEntityId) ? urlEntityId : parseInt(urlEntityId, 10);
      const entityExists = data.some(item => String(item.id) === String(entityId));

      if (entityExists) {
        setSelectedEntityId(entityId);

        // Both 'call' and 'email' panels open RetellCallPanel (which has tabs for both)
        if ((urlPanel === 'call' || urlPanel === 'email') && !showCallModal) {
          setShowCallModal(true);
        } else if (urlPanel === 'edit' && !showFormEditor) {
          setShowFormEditor(true);
        }
      } else {
        navigate(basePath, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlPanel, urlEntityId, data.length]);

  const handleSettingsClick = () => {
    setSidePanel({ isOpen: true, type: 'settings' });
    navigate(`${basePath}?settings=true`);
  };

  const handleCloseSidePanel = () => {
    setSidePanel({ isOpen: false, type: null });
    navigate(basePath);
  };

  // Apply date range preset
  const applyPreset = (presetValue) => {
    const { startDate, endDate } = getDateRangePreset(presetValue);
    setFilters({
      ...filters,
      activity_date_from: startDate,
      activity_date_to: endDate
    });
    setSelectedPreset(presetValue);
  };
  // Clear date range filter
  const clearDateFilter = () => {
    setFilters({
      ...filters,
      activity_date_from: '',
      activity_date_to: ''
    });
    setSelectedPreset(null);
  };

  // Fetch data from backend
  const fetchData = useCallback(async (showLoader = true) => {
    try {
      if (showLoader) {
        dispatch(redux.actions.setLoading(true));
      } else {
        dispatch(redux.actions.setRefreshing(true));
      }
      dispatch(redux.actions.setError(null));

      // Build query params - only include non-empty filters
      // Check URL for limit param to preserve loaded data count on refresh
      const urlLimit = parseInt(searchParams.get('limit')) || 250;
      const queryParams = { limit: urlLimit };
      if (filters.created_by) queryParams.created_by = filters.created_by;
      if (filters.assigned_to) queryParams.assigned_to = filters.assigned_to;
      // is_active: 'true' = active only, 'false' = archived only, 'all' = both
      if (filters.is_active !== '') queryParams.is_active = filters.is_active;
      // Activity date range filters
      if (filters.activity_date_from) queryParams.activity_date_from = filters.activity_date_from;
      if (filters.activity_date_to) queryParams.activity_date_to = filters.activity_date_to;

      const response = await api.getAll(queryParams);

      if (response.data) {
        let items = [];
        let total = 0;

        // Handle new paginated response format: { rows: [...], total: X }
        if (response.data.rows && Array.isArray(response.data.rows)) {
          items = response.data.rows;
          total = response.data.total || 0;
        }
        // Backward compatibility: handle old format (direct array)
        else if (Array.isArray(response.data)) {
          items = response.data;
          total = items.length;
        }
        // Handle nested data formats
        else if (response.data.data && Array.isArray(response.data.data)) {
          items = response.data.data;
          total = items.length;
        } else if (response.data[entityType + 's'] && Array.isArray(response.data[entityType + 's'])) {
          items = response.data[entityType + 's'];
          total = items.length;
        }

        // List API already returns flat, lightweight data - no transform needed
        // transformSalesData is only needed for detail views with lead_data/opportunity_data
        dispatch(redux.actions.setData(items));
        setTotalCount(total);
      } else {
        dispatch(redux.actions.setError('No data received from server'));
      }
    } catch (err) {
      console.error(`Error fetching ${entityNamePlural}:`, err);
      dispatch(redux.actions.setError(err.response?.data?.message || err.message || `Failed to fetch ${entityNamePlural}`));
    } finally {
      dispatch(redux.actions.setLoading(false));
      dispatch(redux.actions.setRefreshing(false));
    }
  }, [api, dispatch, redux, entityType, entityNamePlural, filters.created_by, filters.assigned_to, filters.is_active, filters.activity_date_from, filters.activity_date_to, searchParams]);

  // Fetch data on mount and when filters change
  // Only fetch if both date filters are set or both are empty
  useEffect(() => {
    const hasFromDate = !!filters.activity_date_from;
    const hasToDate = !!filters.activity_date_to;
    if ((hasFromDate && hasToDate) || (!hasFromDate && !hasToDate)) {
      fetchData();
    }
  }, [fetchData, filters.activity_date_from, filters.activity_date_to]);

  // Load more records (append to existing data)
  const loadMoreRecords = useCallback(async () => {
    if (loadingMore || data.length >= totalCount) return;

    try {
      setLoadingMore(true);

      // Build query params with offset to fetch next batch
      const queryParams = {
        limit: 250,
        offset: data.length  // Start from current data length
      };
      if (filters.created_by) queryParams.created_by = filters.created_by;
      if (filters.assigned_to) queryParams.assigned_to = filters.assigned_to;
      if (filters.is_active !== '') queryParams.is_active = filters.is_active;
      if (filters.activity_date_from) queryParams.activity_date_from = filters.activity_date_from;
      if (filters.activity_date_to) queryParams.activity_date_to = filters.activity_date_to;

      const response = await api.getAll(queryParams);

      if (response.data) {
        let newItems = [];
        let total = totalCount;

        // Handle paginated response format
        if (response.data.rows && Array.isArray(response.data.rows)) {
          newItems = response.data.rows;
          total = response.data.total || totalCount;
        } else if (Array.isArray(response.data)) {
          newItems = response.data;
        }

        // Append new items to existing data
        const updatedData = [...dataFromRedux, ...newItems];
        dispatch(redux.actions.setData(updatedData));
        setTotalCount(total);

        // Update URL with new limit to preserve loaded data count on refresh
        const newParams = new URLSearchParams(searchParams);
        newParams.set('limit', updatedData.length.toString());
        setSearchParams(newParams, { replace: true });
      }
    } catch (err) {
      console.error(`Error loading more ${entityNamePlural}:`, err);
      config.onStatusMessage({
        type: 'error',
        message: `Failed to load more ${entityNamePlural.toLowerCase()}: ${err.message}`
      });
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, data.length, totalCount, filters, api, dataFromRedux, dispatch, redux, entityNamePlural, config, searchParams, setSearchParams]);

  // Fetch lead/opportunity statuses and priorities FIRST (blocking)
  // This prevents columnDefs from recalculating after grid renders
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const [statusesRes, prioritiesRes] = await Promise.all([
          adminApi.getLeadOpportunityStatuses(),
          adminApi.getPriorities()
        ]);
        setContactStatuses(statusesRes.data || []);
        setPriorities(prioritiesRes.data || []);
        setSettingsLoaded(true);
      } catch (err) {
        console.error('Error fetching settings:', err);
        // Set empty arrays on error so grid can still render
        setContactStatuses([]);
        setPriorities([]);
        setSettingsLoaded(true);
      }
    };
    fetchSettings();
  }, []);

  // Handle entity update from panels (including new entity creation)
  const handleEntityUpdate = useCallback((updatedEntity) => {
    // Check if this is a new entity (not in current data)
    const existingEntityIndex = dataFromRedux.findIndex(item => item.id === updatedEntity.id);

    if (existingEntityIndex === -1) {
      // New entity - add to the beginning of the list
      const transformedEntity = transformSalesData(updatedEntity);
      dispatch(redux.actions.setData([transformedEntity, ...dataFromRedux]));
    } else {
      // Existing entity - update it
      const updatedData = dataFromRedux.map(item =>
        item.id === updatedEntity.id ? { ...item, ...updatedEntity } : item
      );
      dispatch(redux.actions.setData(updatedData));
    }
  }, [dataFromRedux, dispatch, redux]);

  // Refresh a single entity from DB
  const refreshEntityFromDB = useCallback(async (entityId) => {
    try {
      const response = await api.getOne(entityId);
      if (response.data) {
        const freshEntity = transformSalesData(response.data);
        // Use updateEntity action (updateOpportunity/updateLead) instead of setData
        // This breaks circular dependency: no dependency on dataFromRedux means this function is stable
        dispatch(redux.actions.updateEntity(freshEntity));
      }
    } catch (err) {
      console.error(`Error refreshing ${entityName}:`, err);
    }
  }, [api, dispatch, redux, entityName]); // Removed dataFromRedux dependency

  // Handle delete (archive)
  const handleDelete = async (item) => {
    const hasOutreach = item.activities && item.activities.length > 0;
    const confirmMsg = hasOutreach
      ? `Archive "${item.company_object?.name || item.company}"?\n\nThis ${entityName.toLowerCase()} has ${item.activities.length} outreach activit${item.activities.length === 1 ? 'y' : 'ies'}. Archiving will preserve all data but remove it from active view.`
      : `Archive the ${entityName.toLowerCase()} for "${item.company_object?.name || item.company}"?\n\nYou can restore archived ${entityNamePlural.toLowerCase()} later if needed.`;

    if (!window.confirm(confirmMsg)) {
      return;
    }

    try {
      await api.delete(item.id);
      const updatedData = dataFromRedux.filter(d => d.id !== item.id);
      dispatch(redux.actions.setData(updatedData));
    } catch (err) {
      config.onStatusMessage({
        type: 'error',
        message: `Failed to archive ${entityName.toLowerCase()}: ` + (err.response?.data?.message || err.message)
      });
    }
  };

  // Handle restore (set is_active = true)
  const handleRestore = async (item) => {
    try {
      await api.update(item.id, { is_active: true });
      const updatedData = dataFromRedux.filter(d => d.id !== item.id);
      dispatch(redux.actions.setData(updatedData));
      config.onStatusMessage({
        type: 'success',
        message: `${entityName} for "${item.company_object?.name || item.company}" restored successfully`
      });
    } catch (err) {
      config.onStatusMessage({
        type: 'error',
        message: `Failed to restore ${entityName.toLowerCase()}: ` + (err.response?.data?.message || err.message)
      });
    }
  };

  // Memoized action handlers for ActionsCellRenderer
  const handlePhoneClick = useCallback((entityId) => {
    setSelectedEntityId(entityId);
    setShowCallModal(true);
    navigate(`${basePath}?id=${entityId}&panel=call`);
  }, [basePath, navigate]);

  const handleEmailClick = useCallback((entityId) => {
    setSelectedEntityId(entityId);
    setShowCallModal(true);
    navigate(`${basePath}?id=${entityId}&panel=email`);
  }, [basePath, navigate]);

  const handleEditClick = useCallback((entityId) => {
    setSelectedEntityId(entityId);
    setShowFormEditor(true);
    navigate(`${basePath}?id=${entityId}&panel=edit`);
  }, [basePath, navigate]);

  // Wrapper for ActionsCellRenderer that provides stable props
  const ActionsCellRendererWrapper = useCallback((props) => {
    return (
      <ActionsCellRenderer
        data={props.data}
        activeCallStatuses={activeCallStatusesRef.current}
        entityName={entityName}
        basePath={basePath}
        customActions={config.customActions}
        onPhoneClick={handlePhoneClick}
        onEmailClick={handleEmailClick}
        onEditClick={handleEditClick}
        onArchiveClick={handleDelete}
        onRestoreClick={handleRestore}
      />
    );
  }, [entityName, basePath, config.customActions, handlePhoneClick, handleEmailClick, handleEditClick]);

  // Layout saving callbacks - memoized to prevent unnecessary re-renders
  const handleLayoutSaved = useCallback(() => {
    setAlertState({ show: true, type: 'success', title: '',
      message: 'Table layout has been saved to your preferences'
    });
  }, []);

  const handleLayoutReset = useCallback(() => {
    setAlertState({ show: true, type: 'info', title: '',
      message: 'Table layout has been reset to your saved preferences'
    });
  }, []);

  // Column definitions — only expose stages that apply to this entity type
  // Only recalculate when settings are loaded (prevents unnecessary recalculation)
  const columnDefs = useMemo(() => {
    // Don't build columns until settings are loaded
    if (!settingsLoaded || !contactStatuses || !priorities) {
      return [];
    }

    const filteredStatuses = contactStatuses.filter(
      s => s.applies_to === entityType || s.applies_to === 'both'
    );
    return buildSalesColumns({
      ActionsRenderer: ActionsCellRendererWrapper,
      contactStatuses: filteredStatuses,
      priorities,
      entityType
    });
  }, [settingsLoaded, contactStatuses, priorities, entityType, ActionsCellRendererWrapper]);

  // Handle cell value changed
  const onCellValueChanged = useCallback((params) => {
    const updatedField = params.colDef.field;
    const updatedValue = params.data[updatedField];
    const entityId = params.data.id;

    dispatch(redux.actions.trackCellEdit({
      [`${entityType}Id`]: entityId,
      field: updatedField,
      value: updatedValue
    }));

    params.api.refreshCells({ rowNodes: [params.node], force: true });
  }, [dispatch, redux, entityType]);

  // Save all changes
  const saveAllChanges = useCallback(async () => {
    const editedEntityIds = Object.keys(editedRows);
    if (editedEntityIds.length === 0) {
      return;
    }

    dispatch(redux.actions.setSaving(true));
    const errors = [];

    try {
      for (const entityId of editedEntityIds) {
        const changes = editedRows[entityId];
        try {
          await api.update(entityId, changes);
        } catch (err) {
          errors.push({
            entityId,
            message: err.response?.data?.message || err.message
          });
        }
      }

      if (errors.length === 0) {
        dispatch(redux.actions.clearEditedRows());
        // Patch only the saved rows in Redux using the changes we already sent to the API —
        // no full refetch needed since the server applied exactly what editedRows contained.
        const updatedData = dataFromRedux.map(item => {
          const savedChanges = editedRows[String(item.id)];
          return savedChanges ? { ...item, ...savedChanges } : item;
        });
        dispatch(redux.actions.setData(updatedData));
        config.onStatusMessage({ type: 'success', message: 'All changes saved successfully' });
      } else {
        const errorMessages = errors.map(e => `${entityName} ${e.entityId}: ${e.message}`).join('\n');
        config.onStatusMessage({ type: 'error', message: `Some changes failed to save:\n${errorMessages}` });
      }
    } finally {
      dispatch(redux.actions.setSaving(false));
    }
  }, [editedRows, dataFromRedux, dispatch, redux, api, entityName, config]);

  // Discard changes
  const discardChanges = useCallback(() => {
    const editedEntityIds = Object.keys(editedRows);
    if (editedEntityIds.length === 0) {
      return;
    }

    if (window.confirm('Discard all unsaved changes?')) {
      dispatch(redux.actions.clearEditedRows());
      // Re-dispatch the existing Redux data as a new array reference so the useMemo
      // recomputes fresh mutable copies for AG Grid — no network call needed since
      // cell edits never mutated the Redux store, only the grid's internal state.
      dispatch(redux.actions.setData([...dataFromRedux]));
    }
  }, [editedRows, dataFromRedux, dispatch, redux]);

  return (
    <>
      {/* Alert Message */}
      {alertState.show && (
        <AlertMessage
          show={alertState.show}
          type={alertState.type}
          title={alertState.title}
          message={alertState.message}
          onClose={() => setAlertState({ ...alertState, show: false })}
        />
      )}

      {/* Page Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="mb-sm-0 font-size-18">{entityNamePlural}</h4>
          <p className="text-muted mb-0">
            Manage job posting leads and track sales {entityNamePlural.toLowerCase()}
          </p>
        </div>
        <div className="d-flex gap-2">
          <button
            className="btn btn-success"
            onClick={() => {
              setSelectedEntityId(null);
              setShowFormEditor(true);
              navigate(`${basePath}?panel=create`);
            }}
          >
            <i className="fas fa-plus me-md-2"></i>
            <span className="d-none d-md-inline">Create New</span>
          </button>
          <button
            className="btn btn-outline-primary"
            onClick={() => navigate('/admin/sales/analytics')}
          >
            <i className="fas fa-chart-line me-md-2"></i>
            <span className="d-none d-md-inline">Analytics Dashboard</span>
          </button>
          <button
            className="btn btn-outline-secondary"
            onClick={handleSettingsClick}
          >
            <i className="fas fa-cog me-md-2"></i>
            <span className="d-none d-md-inline">Settings</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="row mb-4">
        <div className="col-md-3">
          <div className="card">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-muted mb-1">Total {entityNamePlural}</h6>
                  <h3 className="mb-0">{loading ? <span className="spinner-border spinner-border-sm"></span> : totalCount}</h3>
                </div>
                <i className="fas fa-briefcase fa-2x text-primary"></i>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-muted mb-1">Qualified</h6>
                  <h3 className="mb-0">{loading ? <span className="spinner-border spinner-border-sm"></span> : data.filter(item => item.contact_status_detail?.name === 'Qualified').length}</h3>
                </div>
                <i className="fas fa-check-circle fa-2x text-success"></i>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-muted mb-1">Won</h6>
                  <h3 className="mb-0">{loading ? <span className="spinner-border spinner-border-sm"></span> : data.filter(item => item.contact_status_detail?.name === 'Won').length}</h3>
                </div>
                <i className="fas fa-trophy fa-2x text-warning"></i>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-muted mb-1">High Priority</h6>
                  <h3 className="mb-0">{loading ? <span className="spinner-border spinner-border-sm"></span> : data.filter(item => item.priority_detail?.name === 'High').length}</h3>
                </div>
                <i className="fas fa-exclamation-circle fa-2x text-danger"></i>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          <i className="fas fa-exclamation-triangle me-2"></i>
          {error}
          <button type="button" className="btn-close" onClick={() => dispatch(redux.actions.setError(null))}></button>
        </div>
      )}

      {/* AG Grid Table */}
      <div className="card">
        <div className="card-body p-0" style={{border:0}}>
          {/* Show loading state while settings are being fetched */}
          {!settingsLoaded && (
            <div className="text-center py-5">
              <div className="spinner-border text-primary mb-2" role="status">
                <span className="visually-hidden">Loading grid settings...</span>
              </div>
              <div className="text-muted">Initializing grid...</div>
            </div>
          )}
          {settingsLoaded && (
            <>
              <div className="d-flex flex-column gap-2 m-2">
                {/* Filter Bar */}
                <div className="d-flex justify-content-between gap-2 flex-wrap">
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <span className="text-muted fw-bold">Status:</span>
              <div className="btn-group" role="group">
                <button type="button" className={`btn btn-sm ${filters.is_active === 'true' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setFilters({ ...filters, is_active: 'true' })}><i className="fas fa-check-circle me-1"></i>Active</button>
                <button type="button" className={`btn btn-sm ${filters.is_active === 'false' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setFilters({ ...filters, is_active: 'false' })}><i className="fas fa-archive me-1"></i>Archived</button>
              </div>
              <div className="vr"></div>
              <span className="text-muted fw-bold">Outreach Activity:</span>
              {dateRangePresets.map(preset => (
                <button key={preset.value} className={`btn btn-sm ${selectedPreset === preset.value ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => applyPreset(preset.value)}>{preset.label}</button>
              ))}
              <input type="date" className="form-control form-control-sm" style={{ width: '140px' }} value={filters.activity_date_from} onChange={(e) => {
                const newFromDate = e.target.value;
                if (filters.activity_date_to && newFromDate && !isValidDateRange(newFromDate, filters.activity_date_to)) {
                  alert('Start date must be less than or equal to end date');
                  return;
                }
                setFilters({ ...filters, activity_date_from: newFromDate });
                setSelectedPreset(null);
              }} />
              <span>to</span>
              <input type="date" className="form-control form-control-sm" style={{ width: '140px' }} value={filters.activity_date_to} onChange={(e) => {
                const newToDate = e.target.value;
                if (filters.activity_date_from && newToDate && !isValidDateRange(filters.activity_date_from, newToDate)) {
                  alert('End date must be greater than or equal to start date');
                  return;
                }
                setFilters({ ...filters, activity_date_to: newToDate });
                setSelectedPreset(null);
              }} />
              {(filters.activity_date_from || filters.activity_date_to) && <button className="btn btn-sm btn-secondary" onClick={clearDateFilter} title="Clear date filter"><i className="fas fa-times"></i></button>}
              </div>

              <div>{editedRowsCount > 0 && (<><button className="btn btn-success btn-sm" onClick={saveAllChanges} disabled={saving}>{saving ? <><span className="spinner-border spinner-border-sm me-2" role="status"></span>Saving...</> : <>Save ({editedRowsCount})</>}</button><button className="btn btn-outline-secondary btn-sm" onClick={discardChanges} disabled={saving}>Discard</button></>)}
              <button className="btn btn-outline-primary btn-sm" onClick={() => fetchData(false)} disabled={refreshing || saving}>{refreshing ? <><span className="spinner-border spinner-border-sm me-2" role="status"></span>Refreshing...</> : <><i className="fas fa-sync-alt me-2"></i>Refresh</>}</button>
              </div>
            </div>
                {/* Action Buttons */}
                <div className="">

                </div>
              </div>
              <div style={{ position: 'relative', minHeight: loading ? '400px' : 'auto' }}>
                {loading && (
                  <div style={{
                    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                    background: 'rgba(255, 255, 255, 0.8)', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', zIndex: 1000
                  }}>
                    <div className="text-center">
                      <div className="spinner-border text-primary mb-2" role="status">
                        <span className="visually-hidden">Loading {entityNamePlural.toLowerCase()}...</span>
                      </div>
                      <div className="text-muted">Loading {entityNamePlural.toLowerCase()}...</div>
                    </div>
                  </div>
                )}
                <SalesAgGridTable
                  ref={gridRef}
                  rowData={data}
                  columnDefs={columnDefs}
                  defaultColDef={defaultColDef}
                  getRowId={(params) => String(params.data.id)}
                  immutableData={true}
                  onCellValueChanged={onCellValueChanged}
                  getRowStyle={(params) => {
                    if (editedRows[params.data?.id]) {
                      return { background: '#fff3cd' };
                    }
                    return null;
                  }}
                  pagination={true}
                  paginationPageSize={50}
                  paginationPageSizeSelector={[25, 50, 100, 200]}
                  enableRangeSelection={true}
                  enableCharts={true}
                  enableLayoutSaving={true}
                  gridId={`${entityNamePlural.toLowerCase().replace(/\s+/g, '_')}_grid`}
                  showLayoutControls={true}
                  controlsPosition="top"
                  onLayoutSaved={handleLayoutSaved}
                  onLayoutReset={handleLayoutReset}
                />

                {/* Load More Button */}
                {totalCount > 0 && data.length < totalCount && (
                  <div className="d-flex justify-content-center mt-3 mb-3">
                    <button
                      className="btn btn-outline-primary"
                      onClick={loadMoreRecords}
                      disabled={loadingMore}
                    >
                      {loadingMore ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Loading...
                        </>
                      ) : (
                        <>Load More ({data.length.toLocaleString()} of {totalCount.toLocaleString()})</>
                      )}
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Form Editor */}
      <EntityEditor
        mode={selectedEntityId ? 'edit' : 'create'}
        entityId={selectedEntityId}
        entity={data.find(item => item.id === selectedEntityId)}
        isOpen={showFormEditor}
        onClose={() => {
          setShowFormEditor(false);
          setSelectedEntityId(null);
          navigate(basePath);
        }}
        onSave={handleEntityUpdate}
        entityType={entityType}
        onNavigate={(direction) => {
          const currentIndex = data.findIndex(item => item.id === selectedEntityId);
          const newIndex = direction === 'next' ? currentIndex + 1 : currentIndex - 1;
          if (newIndex >= 0 && newIndex < data.length) {
            const newEntity = data[newIndex];
            setSelectedEntityId(newEntity.id);
            navigate(`${basePath}?id=${newEntity.id}&panel=edit`);
          }
        }}
        currentIndex={data.findIndex(item => item.id === selectedEntityId)}
        totalEvents={data.length}
      />

      {/* Retell AI Call Panel */}
      <RetellCallPanel
        entityId={selectedEntityId}
        entity={data.find(item => item.id === selectedEntityId)}
        isOpen={showCallModal}
        onClose={() => {
          setShowCallModal(false);
          setSelectedEntityId(null);
          navigate(basePath);
        }}
        onEntityRefresh={refreshEntityFromDB}
        onCallStatusChange={handleCallStatusChange}
        entityType={entityType}
        initialOutreachType={urlPanel === 'email' ? 'email' : 'call'}
        onNavigate={(direction) => {
          const currentIndex = data.findIndex(item => item.id === selectedEntityId);
          const newIndex = direction === 'next' ? currentIndex + 1 : currentIndex - 1;
          if (newIndex >= 0 && newIndex < data.length) {
            const newEntity = data[newIndex];
            setSelectedEntityId(newEntity.id);
            navigate(`${basePath}?id=${newEntity.id}&panel=${urlPanel || 'call'}`);
          }
        }}
        currentIndex={data.findIndex(item => item.id === selectedEntityId)}
        totalEvents={data.length}
      />


      {/* Settings Sidebar Panel */}
      <SidebarPanel
        isOpen={sidePanel.isOpen}
        onClose={handleCloseSidePanel}
        title="Sales Module Settings"
        style={{ width: '900px', maxWidth: '95vw' }}
      >
        {sidePanel.type === 'settings' && (
          <SalesModuleSettings />
        )}
      </SidebarPanel>
    </>
  );
};

export default SalesTablePage;
